/**
 * ShareController - Handles share string and clipboard operations
 * 
 * Manages copy/paste of share codes, clipboard JSON operations, and import/export
 * of patch data via share strings.
 */

import { MODAL_IDS } from '../constants/modals.js';
import { showToast } from './ToastController.js';
import { closeFileMenu } from './MenuController.js';
import {
    createPatchData,
    toShareString,
    fromShareString,
    validatePatchData,
    remapPatchIds,
    extractSubset
} from '../utils/serialization.js';
import {
    PATCHWERK_URL_PREFIX_PROD,
    PATCHWERK_URL_PREFIX_DEV
} from '../constants/index.js';

// Module state - callbacks injected via init
let getDefinitions = null;
let getInstances = null;
let getConnections = null;
let getSelectedDeviceIds = null;
let applyPatchDataFn = null;
let clearStateFn = null;
let renderDeviceFn = null;
let renderAllCablesFn = null;
let updateConnectedPortsFn = null;
let renderLibraryFn = null;
let autosaveFn = null;
let addDefinitionFn = null;
let addInstanceFn = null;
let addConnectionFn = null;

/**
 * Initialize the ShareController
 * @param {Object} deps - Dependencies
 * @param {Function} deps.getDefinitions - Get all template definitions
 * @param {Function} deps.getInstances - Get all device instances
 * @param {Function} deps.getConnections - Get all connections
 * @param {Function} deps.getSelectedDeviceIds - Get currently selected device IDs (Set)
 * @param {Function} deps.applyPatchData - Apply full patch data (for replace mode)
 * @param {Function} deps.clearState - Clear all state (for replace mode)
 * @param {Function} deps.renderDevice - Render a single device
 * @param {Function} deps.renderAllCables - Re-render all cables
 * @param {Function} deps.updateConnectedPorts - Update connected port styling
 * @param {Function} deps.renderLibrary - Re-render library panel
 * @param {Function} deps.autosave - Trigger autosave
 * @param {Function} deps.addDefinition - Add a definition to the model
 * @param {Function} deps.addInstance - Add an instance to the model
 * @param {Function} deps.addConnection - Add a connection to the model
 * @returns {Object} Public methods for onclick handlers
 */
export function initShareController(deps) {
    getDefinitions = deps.getDefinitions;
    getInstances = deps.getInstances;
    getConnections = deps.getConnections;
    getSelectedDeviceIds = deps.getSelectedDeviceIds;
    applyPatchDataFn = deps.applyPatchData;
    clearStateFn = deps.clearState;
    renderDeviceFn = deps.renderDevice;
    renderAllCablesFn = deps.renderAllCables;
    updateConnectedPortsFn = deps.updateConnectedPorts;
    renderLibraryFn = deps.renderLibrary;
    autosaveFn = deps.autosave;
    addDefinitionFn = deps.addDefinition;
    addInstanceFn = deps.addInstance;
    addConnectionFn = deps.addConnection;
    
    // Return public methods for window/onclick exposure
    return {
        // Share string operations
        copyPatchShareString,
        copySelectionShareString,
        showImportShareStringDialog,
        closeShareStringModal,
        importShareStringFromModal,
        importPatchFromShareString,
        // Clipboard JSON operations
        saveToClipboard,
        openFromClipboard,
        importFromClipboard,
        copyClipboardJson,
        closeClipboardModal,
        // URL handling
        loadPatchFromUrl
    };
}

// ============= URL PATCH LOADING =============

/**
 * Check URL for patch parameter and load it if present.
 * Supports both query param (?patch=...) and path-style (/patch=...)
 * 
 * @returns {Promise<boolean>} True if a patch was loaded from URL
 */
async function loadPatchFromUrl() {
    try {
        const url = new URL(window.location.href);
        let shareString = null;
        
        // Check query parameter: ?patch=PW1~...
        if (url.searchParams.has('patch')) {
            shareString = url.searchParams.get('patch');
        }
        
        // Check path-style: /patch=PW1~...
        // The URL format https://patchwerk.fly.dev/patch=PW1~... has patch= in pathname
        if (!shareString && url.pathname.includes('/patch=')) {
            const match = url.pathname.match(/\/patch=(.+)/);
            if (match) {
                shareString = decodeURIComponent(match[1]);
            }
        }
        
        // Also check hash: #patch=PW1~...
        if (!shareString && url.hash.startsWith('#patch=')) {
            shareString = decodeURIComponent(url.hash.slice(7));
        }
        
        if (!shareString) return false;
        
        // Validate it looks like a share string
        if (!shareString.startsWith('PW1~')) {
            console.warn('[ShareController] Invalid patch in URL - expected PW1~ prefix');
            return false;
        }
        
        // Parse and validate
        const data = await fromShareString(shareString);
        const validation = validatePatchData(data);
        
        if (!validation.valid) {
            showToast('Invalid patch in URL: ' + (validation.error || 'Unknown error'), true);
            return false;
        }
        
        if (validation.needsMigration) {
            showToast('Patch in URL is too old to load', true);
            return false;
        }
        
        // Load the patch (replace mode)
        const success = applyPatchDataFn(data, false);
        
        if (success) {
            // Clean up the URL to avoid reloading on refresh
            // Use replaceState to avoid adding to browser history
            const cleanUrl = url.origin + url.pathname.replace(/\/patch=.+/, '');
            window.history.replaceState({}, '', cleanUrl);
            
            showToast('Loaded patch from shared link!');
            autosaveFn?.();
            return true;
        } else {
            showToast('Failed to load patch from URL', true);
            return false;
        }
    } catch (e) {
        console.warn('[ShareController] Failed to load patch from URL:', e);
        showToast('Failed to load shared patch: ' + e.message, true);
        return false;
    }
}

// ============= SHARE STRING FUNCTIONS =============

/**
 * Copy entire current patch as a share string to clipboard
 */
async function copyPatchShareString() {
    try {
        const patchData = createPatchData({
            templates: getDefinitions(),
            devices: getInstances(),
            connections: getConnections()
        });
        
        const shareString = await toShareString(patchData);
        await navigator.clipboard.writeText(shareString);
        showToast('Share code copied to clipboard!');
    } catch (e) {
        showToast('Failed to create share code: ' + e.message, true);
    }
}

/**
 * Copy selected devices as a share string to clipboard
 */
async function copySelectionShareString() {
    try {
        const selectedIds = getSelectedDeviceIds();
        if (!selectedIds || selectedIds.size === 0) {
            showToast('No devices selected', true);
            return;
        }
        
        const deviceIds = selectedIds;
        const patchData = extractSubset({
            deviceIds,
            allDevices: getInstances(),
            allTemplates: getDefinitions(),
            allConnections: getConnections(),
            internalConnectionsOnly: true
        });
        
        const shareString = await toShareString(patchData);
        await navigator.clipboard.writeText(shareString);
        showToast(`Share code copied (${deviceIds.size} devices)!`);
    } catch (e) {
        showToast('Failed to create share code: ' + e.message, true);
    }
}

/**
 * Show modal dialog to import a share string
 * @param {boolean} mergeMode - If true, merge into current patch; if false, replace
 */
function showImportShareStringDialog(mergeMode = true) {
    const modal = document.getElementById(MODAL_IDS.SHARE_STRING);
    const textarea = document.getElementById('share-string-input');
    const title = document.getElementById('share-string-modal-title');
    
    title.textContent = mergeMode ? '📥 Import Share Code (Merge)' : '📥 Import Share Code (Replace)';
    textarea.value = '';
    textarea.dataset.mergeMode = mergeMode;
    
    modal.classList.add('active');
    textarea.focus();
}

/**
 * Close the share string import modal
 */
function closeShareStringModal() {
    document.getElementById(MODAL_IDS.SHARE_STRING).classList.remove('active');
}

/**
 * Import patch data from a share string (called from modal's import button)
 */
async function importShareStringFromModal() {
    const textarea = document.getElementById('share-string-input');
    const shareString = textarea.value.trim();
    const mergeMode = textarea.dataset.mergeMode === 'true';
    
    if (!shareString) {
        showToast('Please paste a share code', true);
        return;
    }
    
    await importPatchFromShareString(shareString, mergeMode);
}

/**
 * Import patch data from a share string
 * @param {string} shareString - The encoded share string
 * @param {boolean} mergeMode - If true, merge into current patch; if false, replace
 */
async function importPatchFromShareString(shareString, mergeMode = true) {
    try {
        const data = await fromShareString(shareString);
        
        const validation = validatePatchData(data);
        if (!validation.valid) {
            throw new Error(validation.error || 'Invalid share code');
        }
        
        closeShareStringModal();
        
        if (mergeMode) {
            // Merge mode: remap IDs and add to existing patch
            const remapped = remapPatchIds(data);
            
            // Add templates (skip if already exist by name)
            const existingDefs = getDefinitions();
            remapped.templates.forEach(t => {
                if (!existingDefs.some(existing => existing.name === t.name)) {
                    addDefinitionFn(t);
                }
            });
            
            // Add devices
            remapped.devices.forEach(d => {
                addInstanceFn(d);
                renderDeviceFn(d);
            });
            
            // Add connections
            if (remapped.connections) {
                remapped.connections.forEach(c => {
                    addConnectionFn(c);
                });
                renderAllCablesFn();
                updateConnectedPortsFn();
            }
            
            renderLibraryFn();
            autosaveFn();
            showToast(`Imported ${remapped.devices.length} devices`);
        } else {
            // Replace mode: use applyPatchData callback
            const success = applyPatchDataFn(data, false);
            if (success) {
                autosaveFn();
                showToast('Loaded patch from share code');
            } else {
                showToast('Failed to load patch', true);
            }
        }
    } catch (e) {
        showToast('Failed to import: ' + e.message, true);
    }
}

// ============= CLIPBOARD JSON FUNCTIONS =============

/**
 * Save current patch as JSON to clipboard (shows modal with JSON)
 */
function saveToClipboard() {
    closeFileMenu();
    const data = createPatchData({
        templates: getDefinitions(),
        devices: getInstances(),
        connections: getConnections()
    });
    const json = JSON.stringify(data, null, 2);
    
    // Copy to clipboard
    navigator.clipboard.writeText(json).then(() => {
        // Show modal with JSON
        const modal = document.getElementById(MODAL_IDS.CLIPBOARD);
        const textarea = document.getElementById('clipboard-json');
        textarea.value = json;
        modal.classList.add('active');
    }).catch(() => {
        // Fallback: just show modal
        const modal = document.getElementById(MODAL_IDS.CLIPBOARD);
        const textarea = document.getElementById('clipboard-json');
        textarea.value = json;
        modal.classList.add('active');
    });
}

/**
 * Copy JSON from clipboard modal textarea to clipboard
 */
function copyClipboardJson() {
    const textarea = document.getElementById('clipboard-json');
    textarea.select();
    navigator.clipboard.writeText(textarea.value).then(() => {
        const status = document.getElementById('copy-status');
        status.classList.add('show');
        setTimeout(() => status.classList.remove('show'), 2000);
    });
}

/**
 * Close the clipboard JSON modal
 */
function closeClipboardModal() {
    document.getElementById(MODAL_IDS.CLIPBOARD).classList.remove('active');
}

/**
 * Open patch from clipboard (reads JSON or share string, replaces current patch)
 */
async function openFromClipboard() {
    closeFileMenu();
    try {
        const text = await navigator.clipboard.readText();
        if (!text) {
            showToast('Clipboard is empty', true);
            return;
        }
        
        let data;
        // Auto-detect format: share string (PW1~...) or JSON
        if (text.trim().startsWith('PW1~')) {
            data = await fromShareString(text);
        } else {
            data = JSON.parse(text);
        }
        
        const validation = validatePatchData(data);
        if (!validation.valid) {
            throw new Error(validation.error || 'Invalid patch data');
        }
        if (validation.needsMigration) {
            throw new Error('Patch format too old - please use a newer export');
        }
        
        // Clear and load via applyPatchData
        const success = applyPatchDataFn(data, false);
        if (success) {
            autosaveFn();
            showToast('Opened patch from clipboard');
        } else {
            showToast('Failed to open patch', true);
        }
    } catch (e) {
        showToast('Failed to parse clipboard: ' + e.message, true);
    }
}

/**
 * Import/merge patch from clipboard (reads JSON or share string, merges with current)
 */
async function importFromClipboard() {
    closeFileMenu();
    try {
        const text = await navigator.clipboard.readText();
        if (!text) {
            showToast('Clipboard is empty', true);
            return;
        }
        
        let data;
        // Auto-detect format: share string (PW1~...) or JSON
        if (text.trim().startsWith('PW1~')) {
            data = await fromShareString(text);
        } else {
            data = JSON.parse(text);
        }
        
        const validation = validatePatchData(data);
        if (!validation.valid) {
            throw new Error(validation.error || 'Invalid patch data');
        }
        if (validation.needsMigration) {
            throw new Error('Patch format too old - please use a newer export');
        }
        
        // Merge with ID remapping
        const remapped = remapPatchIds(data);
        
        remapped.templates.forEach(t => addDefinitionFn(t));
        remapped.devices.forEach(d => {
            addInstanceFn(d);
            renderDeviceFn(d);
        });
        remapped.connections.forEach(c => addConnectionFn(c));
        
        renderAllCablesFn();
        updateConnectedPortsFn();
        renderLibraryFn();
        autosaveFn();
        
        showToast(`Imported ${remapped.devices.length} devices from clipboard`);
    } catch (e) {
        showToast('Failed to import: ' + e.message, true);
    }
}
